import { Link } from 'react-router-dom'
import { Cartao } from '../../../components/ListaDeDados'
import { ROTULO_ORIGEM_DEMANDA, TEXTOS } from '../../../lib/glossario'
import type { ChamadoForaDaApuracao, ChamadoSemJulgamento, ContagensDaCompetencia } from '../../../types/chamado'
import { ForaDaApuracao } from './ForaDaApuracao'
import { enderecoDaCompetencia, enderecoDaFilaDeJulgamento } from './indicadores'

interface Props {
  competencia: string
  contagens: ContagensDaCompetencia
  semJulgamento: ChamadoSemJulgamento[]
  /** Os que o fiscal tirou do mês. Ausente em fechamento congelado antes da versão 1.2. */
  foraDaApuracao?: ChamadoForaDaApuracao[]
}

/**
 * Os denominadores por extenso, e quem ficou de fora deles.
 *
 * As contagens explicam como se chegou a cada fração: por que o denominador do indicador de prazos
 * é menor que o total de chamados (a manutenção corretiva sai dele, TR p.152), e quantas entregas
 * tiveram aceite integral e parcial. Um indicador traz a fração; isto traz a conta.
 *
 * A lista de chamados sem análise da fiscalização vem junto, e nomeada um a um. Eles fecharam no mês
 * e ficaram fora de TODOS os denominadores acima — na planilha de maio são 25 das 70 linhas, todas
 * com as colunas de julgamento em branco e a observação "chamado fechado em junho/2026". Sumir com
 * eles em silêncio é como um mês perde chamado sem ninguém ver.
 *
 * @param props a competência, as contagens e os chamados sem julgamento
 * @returns o cartão dos denominadores
 */
export function ContagensDoMes({ competencia, contagens, semJulgamento, foraDaApuracao }: Props) {
  return (
    <Cartao
      titulo="A conta por trás dos números"
      descricao="De onde saem os denominadores de cada indicador deste mês."
      acao={
        <Link
          to={enderecoDaCompetencia(competencia)}
          className="text-sm font-medium text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
        >
          Ver os chamados do mês
        </Link>
      }
    >
      <div className="space-y-5">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          <Contagem rotulo="Chamados analisados no mês" valor={contagens.chamados} />
          <Contagem rotulo="Manutenções corretivas" valor={contagens.manutencoesCorretivas} />
          <Contagem rotulo="Entregas com inconformidade" valor={contagens.comInconformidade} />
          <Contagem rotulo="Atendidos no prazo" valor={contagens.atendidosNoPrazo} />
          <Contagem rotulo="Aceites integrais" valor={contagens.aceitesIntegrais} />
          <Contagem rotulo="Aceites parciais" valor={contagens.aceitesParciais} />
          {/* Informativo, e não um denominador: a premissa de tirar os projetos do indicador de
              prazos da sustentação ainda não foi confirmada pelo gestor do contrato. */}
          <Contagem rotulo={ROTULO_ORIGEM_DEMANDA.PROJETO ?? 'Projeto'} valor={contagens.deProjeto} />
          {/* As três da V47: ausentes em fechamento congelado antes dela, e então não aparecem. */}
          {contagens.aceitesAposTentativas !== undefined && (
            <Contagem rotulo="Aceites após 3 tentativas" valor={contagens.aceitesAposTentativas} />
          )}
          {contagens.foraDoIap !== undefined && <Contagem rotulo="Fora do IAP (não se aplica)" valor={contagens.foraDoIap} />}
          {contagens.foraDoIae !== undefined && <Contagem rotulo="Fora do IAE (não se aplica)" valor={contagens.foraDoIae} />}
        </dl>

        {semJulgamento.length > 0 && <SemJulgamento competencia={competencia} pendentes={semJulgamento} />}
        <ForaDaApuracao chamados={foraDaApuracao} />
      </div>
    </Cartao>
  )
}

/** Uma contagem. Zero aparece escrito: a ausência do número não afirma nada. */
function Contagem({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-slate-500">{rotulo}</dt>
      <dd className="mt-1 text-xl font-bold tabular-nums text-slate-900">{valor}</dd>
    </div>
  )
}

/**
 * Os chamados que fecharam no mês e ninguém julgou — fora de todo denominador.
 *
 * **Cada número leva DIRETO ao chamado, e não a uma busca por ele.** O link ia para
 * `/chamados?q=<numero>`, uma listagem de um item de onde ainda era preciso clicar: com 107
 * pendências em agosto/2026, mais de duzentas navegações a mais para fechar o mês. O `id` viaja na
 * resposta ao lado do número justamente para isto.
 *
 * **E o cabeçalho leva à fila inteira**, filtrada na listagem — que é onde a etiqueta "Sem
 * julgamento" e o chip existem. Esta caixa diz QUANTOS faltam; a lista é onde se trabalha.
 */
function SemJulgamento({ competencia, pendentes }: { competencia: string; pendentes: ChamadoSemJulgamento[] }) {
  return (
    <section className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
      <h3 className="text-sm font-semibold text-amber-900">
        {TEXTOS.fiscalizacao.semJulgamento.titulo} ({pendentes.length})
      </h3>
      <p className="mt-1 max-w-prose text-xs text-amber-900">{TEXTOS.fiscalizacao.semJulgamento.ajuda}</p>
      <Link
        to={enderecoDaFilaDeJulgamento(competencia)}
        className="mt-2 inline-block text-sm font-medium text-niteroi-800 underline underline-offset-2 hover:text-niteroi-900"
      >
        {TEXTOS.fiscalizacao.semJulgamento.verAFila}
      </Link>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {pendentes.map((chamado) => (
          <li key={chamado.idExterno}>
            <Link
              to={`/chamados/${chamado.id}`}
              className="text-sm tabular-nums text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
            >
              {chamado.idExterno}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
